import React from 'react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Tooltip } from '../../../components/ui/tooltip';
import { configurationConstants } from '../constants/configurationConstants';
import { commonConstants } from '../constants/commonConstants';
import { getSequenceColumns, generateSequenceData } from '../helpers/sequenceHelpers';
import SequenceData from './SequenceData';

interface SequenceTableProps {
  selectedConfiguration: any;
  sequenceList: any[];
  selectionType: string;
  generateSequence: () => void;
  doesHavePermissionToGenerate: boolean;
  hasMoreSubSpreads: boolean;
  currentPage: number;
  rowsPerPage: number;
  totalCount: number;
  handleTableChange: (pagination: any) => void;
  isSequenceApproved: boolean;
  doesHavePermissionToViewApprove: boolean;
  projectNumber: string;
  pipeCounts: any;
  onOpenSequenceReport: () => void;
  onOpenUpdateSequence: () => void;
  onOpenSequenceSettings: () => void;
  canReport?: boolean;
  sequenceViewMode: 'graph' | 'table';
  onSequenceViewModeChange: (mode: 'graph' | 'table') => void;
  graphRecordList: any[];
  graphPipesPerRow: number;
  onGraphPipesPerRowChange: (pipesPerRow: number) => void;
  isGraphPanelLoading: boolean;
  sequenceLoadingMore?: boolean;
  graphFetchError: string | null;
  onRetryGraphFetch: () => void;
}

export const SequenceTable: React.FC<SequenceTableProps> = ({
  selectedConfiguration,
  sequenceList,
  selectionType,
  generateSequence,
  doesHavePermissionToGenerate,
  hasMoreSubSpreads,
  currentPage,
  rowsPerPage,
  totalCount,
  handleTableChange,
  isSequenceApproved,
  doesHavePermissionToViewApprove,
  projectNumber,
  pipeCounts,
  onOpenSequenceReport,
  onOpenUpdateSequence,
  onOpenSequenceSettings,
  canReport = true,
  sequenceViewMode,
  onSequenceViewModeChange,
  graphRecordList,
  graphPipesPerRow,
  onGraphPipesPerRowChange,
  isGraphPanelLoading,
  sequenceLoadingMore,
  graphFetchError,
  onRetryGraphFetch,
}) => {
  const getNameBasedOnType = (type: string): string => {
    switch (type) {
      case configurationConstants.selectionType.spread:
        return selectedConfiguration.spreadName;
      case configurationConstants.selectionType.subSpread:
        return selectedConfiguration.subSpreadName;
      default:
        return '';
    }
  };

  const customPendingStatusTextV = (): string => {
    const { pipeCount: projectPipeCount, spreadPipeCount, subSpreadPipeCount } = pipeCounts || {};

    if (projectPipeCount === 0) {
      return 'Pipe data not available. Please upload pipe data first.';
    } else if (selectionType === configurationConstants.selectionType.spread) {
      const sufficientData = selectedConfiguration.targetNumberOfPipes <= (spreadPipeCount || 0);
      return `Pipe data ${sufficientData ? 'sufficient' : 'insufficient'} for ${getNameBasedOnType(selectionType)}. Do you want to generate with available data?`;
    } else {
      const sufficientData = selectedConfiguration.targetNumberOfPipes <= (subSpreadPipeCount || 0);
      return `Pipe data ${sufficientData ? 'sufficient' : 'insufficient'} for ${getNameBasedOnType(selectionType)}. Do you want to generate with available data?`;
    }
  };

  const customPendingStatusText = (
    <div className="space-y-4 p-6 text-center">
      <p>{customPendingStatusTextV()}</p>
      {pipeCounts?.pipeCount ? (
        <Button onClick={generateSequence}>Yes</Button>
      ) : null}
    </div>
  );

  const customerErrorStatusText = (
    <div className="space-y-4 p-6 text-center">
      <p>
        Sequence failed for the reason:{' '}
        {selectedConfiguration?.reasonForError || 'Some unknown error'}
      </p>
      <p>Do you want to regenerate the sequence for {getNameBasedOnType(selectionType)}?</p>
      <Button onClick={generateSequence}>Yes</Button>
    </div>
  );

  const getOtherStatusText = (status: string) => (
    <div className="p-6 text-center">
      <span>Current status of sequence generation is: {status}</span>
    </div>
  );

  const getSequenceGenerationTextBasedOnStatus = (status: string) => {
    if (!doesHavePermissionToGenerate) {
      return <div className="p-6 text-center">Please wait till the sequence is generated.</div>;
    }
    switch (status) {
      case configurationConstants.sequenceGenerationStatus.Pending:
        return customPendingStatusText;
      case configurationConstants.sequenceGenerationStatus.ErrorOccurred:
        return customerErrorStatusText;
      default:
        return getOtherStatusText(status);
    }
  };

  const spreadName = () => {
    if (selectionType === configurationConstants.selectionType.spread) {
      return selectedConfiguration.spreadName;
    }
    return '';
  };

  if (
    selectedConfiguration?.sequenceGenerationStatus !== configurationConstants.sequenceGenerationStatus.Completed &&
    !hasMoreSubSpreads
  ) {
    return doesHavePermissionToViewApprove ? (
      <div className="p-6 text-center">
        <span>Sequence yet to be generated</span>
      </div>
    ) : (
      getSequenceGenerationTextBasedOnStatus(selectedConfiguration?.sequenceGenerationStatus || '')
    );
  }

  if (hasMoreSubSpreads && sequenceList?.length === 0) {
    return (
      <div className="p-6 text-center">
        <span>Sequence not ready. Generate sequences for all sub-sequences first.</span>
      </div>
    );
  }

  return (
    <div>
        <SequenceData
          sequenceList={sequenceList}
          selectionType={selectionType}
          spreadName={spreadName()}
          currentPage={currentPage}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount}
          handleTableChange={handleTableChange}
          canEdit={doesHavePermissionToViewApprove}
          onOpenSequenceReport={onOpenSequenceReport}
          onOpenUpdateSequence={onOpenUpdateSequence}
          onOpenSequenceSettings={onOpenSequenceSettings}
          canReport={canReport}
          hasSelectedSequence={Boolean(selectedConfiguration?.id)}
          sequenceViewMode={sequenceViewMode}
          onSequenceViewModeChange={onSequenceViewModeChange}
          graphRecordList={graphRecordList}
          graphPipesPerRow={graphPipesPerRow}
          onGraphPipesPerRowChange={onGraphPipesPerRowChange}
          isGraphPanelLoading={isGraphPanelLoading}
          sequenceLoadingMore={sequenceLoadingMore}
          graphFetchError={graphFetchError}
          onRetryGraphFetch={onRetryGraphFetch}
          defaultOD={selectedConfiguration?.nominalOdMax}
        />
    </div>
  );
};
